import {t, Trans} from '@lingui/macro';
import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {
  LocalizeRenderer,
  useLocalize,
} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {buildSlugError, SlugErrorsEnum} from '../../../../helpers/models/schedulingLinkHelpers';
import {Localizer} from '../../../../store/stateless/internationalizationSelectors';
import {SchedulingLinkBoxedInputWrapper} from './schedulingLinkBoxedInputWrapper';
import {SchedulingLinkDetailRow} from './schedulingLinkDetailRow';
import {SchedulingLinkSettingsSection} from './schedulingLinkSettingsSection';

/*
 * Constants.
 */

export const SlugErrorRenderers: {[T in SlugErrorsEnum]: LocalizeRenderer} = {
  [SlugErrorsEnum.DUPLICATE]: (localize) =>
    localize(t('scheduling-settings-slugerror-duplicate')`This URL is already used`),
  [SlugErrorsEnum.INVALID_CONTENT]: (localize) =>
    localize(
      t(
        'schedulinglink-settings-slugerror-invalidcontent',
      )`URL can only contain alphanumeric characters (no spaces), dashes, underscores, and must be less than 25 characters`,
    ),
};

/*
 * Props.
 */

export interface SchedulingLinkUrlSettingsProps {
  isNewSchedulingLink: boolean;
  name: string;
  slug: string;
  otherSlugs: ReadonlyArray<string>;
  userAlias: string;
  companySubdomain: string;
  onSlugChange: HandlerOf<string>;
  onNameChange: HandlerOf<string>;
}

/*
 * Style.
 */

const StyledRowsWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 20px;
  padding-bottom: 20px;
`;

const StyledSlugInputWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 10px;
`;

const StyledUrlDiv = styled.div`
  overflow-wrap: anywhere;
`;

const StyledUrlSlugSpan = styled.span`
  font-weight: ${fontWeights.medium};
`;

const StyledSlugErrorDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.palette.red.shade40};
  margin-top: 5px;
`;

/*
 * Component.
 */

export const SchedulingLinkUrlSettings: FC<SchedulingLinkUrlSettingsProps> = (props) => {
  const localize = useLocalize();
  const slugError = useMemo(
    () => buildSlugErrorText(localize, props.slug, props.otherSlugs),
    [localize, props.slug, props.otherSlugs, buildSlugErrorText],
  );

  return (
    <SchedulingLinkSettingsSection>
      <StyledRowsWrapperDiv>
        {renderNameRow(localize, props)}
        {renderSlugRow(localize, props, slugError)}
      </StyledRowsWrapperDiv>
    </SchedulingLinkSettingsSection>
  );
};

function renderNameRow(localize: Localizer, props: SchedulingLinkUrlSettingsProps) {
  return (
    <SchedulingLinkDetailRow
      label={<Trans id="schedulinglink-settings-name">Name</Trans>}
      renderTooltip={() => (
        <Trans id="schedulinglink-settings-nametooltip">
          Add a name to reference this scheduling link by. This name is not shown to visitors.
        </Trans>
      )}
    >
      <SchedulingLinkBoxedInputWrapper>
        <TextBox
          placeholder={localize(t('schedulinglink-settings-name-placeholder')`Name this scheduling link`)}
          defaultValue={props.name}
          onChange={props.onNameChange}
        />
      </SchedulingLinkBoxedInputWrapper>
    </SchedulingLinkDetailRow>
  );
}

function renderSlugRow(localize: Localizer, props: SchedulingLinkUrlSettingsProps, slugError: string | null) {
  const url = `cal.frontapp.com/${props.companySubdomain}/${props.userAlias}/`;

  return (
    <SchedulingLinkDetailRow
      label={<Trans id="schedulinglink-settings-slug">URL</Trans>}
      renderTooltip={() => (
        <Trans id="schedulinglink-settings-slugtooltip">
          This is the link you will share with others so they can find time to meet with you.
        </Trans>
      )}
    >
      <StyledSlugInputWrapperDiv>
        {props.isNewSchedulingLink && (
          <SchedulingLinkBoxedInputWrapper>
            <TextBox
              placeholder={localize(t('schedulinglink-settings-slug-placeholder')`Add a custom URL`)}
              defaultValue={props.slug || undefined}
              onChange={props.onSlugChange}
              hasError={Boolean(slugError)}
              errorMessage={slugError && <StyledSlugErrorDiv>{slugError}</StyledSlugErrorDiv>}
            />
          </SchedulingLinkBoxedInputWrapper>
        )}
        <StyledUrlDiv>
          <span>
            {url}
            <StyledUrlSlugSpan>{props.slug}</StyledUrlSlugSpan>
          </span>
        </StyledUrlDiv>
      </StyledSlugInputWrapperDiv>
    </SchedulingLinkDetailRow>
  );
}

function buildSlugErrorText(localize: Localizer, slug: string, otherSlugs: ReadonlyArray<string>) {
  const slugError = buildSlugError(slug, otherSlugs);

  if (!slugError) {
    return null;
  }

  return SlugErrorRenderers[slugError](localize);
}
